import type { Action } from "redux";
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  merge,
  mergeMap,
  switchMap,
  takeUntil,
  type Observable,
} from "rxjs";
import { concat, of } from "rxjs";
import type { StateObservable } from "redux-observable";
import type { RootState } from "@/store/app/storeTypes";
import {
  requestError,
  requestStart,
  requestSuccess,
} from "@/store/requests/requestsSlice";
import { createPollingStream } from "@/streams/createPollingStream";
import type { UserProfile } from "@/hooks/useUserProfile";
import { isUserProfileSessionReady } from "@/lib/session/isUserProfileSessionReady";
import { profileFromState } from "@/store/epics/profileFromState";
import { extractHttpStatusFromUnknown } from "@/lib/http/extractHttpStatusFromUnknown";
import type { CatalogPeriod } from "@/constants/sparxsCatalogFilters";

export const toRequestErrorAction = (
  requestKey: string,
  subKey: string,
  err: unknown,
) => {
  const message = err instanceof Error ? err.message : String(err);
  const httpStatus = extractHttpStatusFromUnknown(err);
  return requestError({
    requestKey,
    subKey,
    error: message,
    ...(httpStatus !== undefined ? { httpStatus } : {}),
  });
};

export const pollRequestSubKey = <TData>(
  requestKey: string,
  subKey: string,
  fetch: () => Observable<TData>,
  intervalMs: number,
) =>
  concat(
    of(requestStart({ requestKey, subKey })),
    createPollingStream(intervalMs, () =>
      fetch().pipe(
        map((data) =>
          requestSuccess({
            requestKey,
            subKey,
            data,
          }),
        ),
        catchError((err: unknown) =>
          of(toRequestErrorAction(requestKey, subKey, err)),
        ),
      ),
    ),
  );

export type PeriodScopedPollStream = {
  requestKey: string;
  subKey: string;
  fetch: (profile: UserProfile, period: CatalogPeriod) => Observable<unknown>;
};

export type WatchPeriodScopedPollStreamsInput = {
  state$: StateObservable<RootState>;
  cancel$: Observable<Action>;
  intervalMs: number;
  streams: readonly PeriodScopedPollStream[];
};

export const watchPeriodScopedPollStreams = ({
  state$,
  cancel$,
  intervalMs,
  streams,
}: WatchPeriodScopedPollStreamsInput): Observable<Action> =>
  state$.pipe(
    map((state) => ({
      period: state.statistics.periodFilter,
      profile: profileFromState(state),
    })),
    filter(({ profile }) => isUserProfileSessionReady(profile)),
    distinctUntilChanged(
      (a, b) =>
        a.period === b.period &&
        a.profile.currentFinancialInstitution ===
          b.profile.currentFinancialInstitution,
    ),
    switchMap(({ period, profile }) => {
      const userProfile = profile as UserProfile;
      return merge(
        ...streams.map(({ requestKey, subKey, fetch }) =>
          pollRequestSubKey(
            requestKey,
            subKey,
            () => fetch(userProfile, period),
            intervalMs,
          ),
        ),
      );
    }),
    takeUntil(cancel$),
  );

export type PeriodScopedPollingSubscriptionInput = {
  action$: Observable<Action>;
  state$: StateObservable<RootState>;
  requestedType: string;
  cancelledType: string;
  intervalMs: number;
  streams: readonly PeriodScopedPollStream[];
};

export const createPeriodScopedPollingSubscription = ({
  action$,
  state$,
  requestedType,
  cancelledType,
  intervalMs,
  streams,
}: PeriodScopedPollingSubscriptionInput): Observable<Action> =>
  action$.pipe(
    filter((a): a is Action => a.type === requestedType),
    mergeMap(() => {
      const cancel$ = action$.pipe(
        filter((a): a is Action => a.type === cancelledType),
      );
      return watchPeriodScopedPollStreams({
        state$,
        cancel$,
        intervalMs,
        streams,
      });
    }),
  );
